import { useEffect } from 'react';

import {
  Button,
  ButtonColors,
  ButtonSizes,
  ButtonVariants,
  Modal,
} from '@repo/ui';
import { LinkButton, Typography } from 'components';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';

import { urls } from '@repo/app-constants';
import VideoResource from 'types/VideoResource';

import {
  UPSELL_BENEFITS,
  UPSELL_CHECKOUT_SOURCE,
  UPSELL_SOURCE,
} from '../VideoResources.helpers';
import styles from './UpgradeModal.module.css';
import { UpsellCarousel } from './UpsellCarousel';

const buildCheckoutUrl = (base: string, source: string) => {
  const separator = base.includes('?') ? '&' : '?';
  return `${base}${separator}source=${source}`;
};

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  exclusiveVideos: VideoResource[];
  triggerVideoId?: number;
}

const UpgradeModal = ({
  isOpen,
  onClose,
  exclusiveVideos,
  triggerVideoId,
}: UpgradeModalProps) => {
  const { trackEvent } = useAnalytics();
  const upgradeLink = buildCheckoutUrl(
    urls.TINY_PLUS_UPGRADE_OFFER,
    UPSELL_CHECKOUT_SOURCE,
  );

  useEffect(() => {
    if (!isOpen) return;
    trackEvent('Tiny Plus Upsell Modal Viewed', {
      source: UPSELL_SOURCE,
      resource_id: triggerVideoId,
    });
  }, [isOpen, triggerVideoId, trackEvent]);

  const handleUpgradeClick = () => {
    trackEvent('Tiny Plus Upsell CTA Clicked', {
      source: UPSELL_SOURCE,
      cta: 'upgrade',
      resource_id: triggerVideoId,
    });
  };

  const handleDismiss = () => {
    trackEvent('Tiny Plus Upsell CTA Clicked', {
      source: UPSELL_SOURCE,
      cta: 'dismiss',
      resource_id: triggerVideoId,
    });
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className={styles.modalDialog}
      hasBodyPadding={false}
    >
      <div className={styles.modalBody}>
        <UpsellCarousel videos={exclusiveVideos} />

        <div className={styles.content}>
          <div className={styles.intro}>
            <Typography variant='heading-l' className={styles.heading}>
              Learn live.
              <br />
              Ask questions, get answers.
              <br />
              Rewatch anytime.
            </Typography>
            <Typography variant='body-s' color='grey'>
              Tiny+ members get access to our quarterly exclusive webinars in
              addition to our regular webinars.
            </Typography>
          </div>

          <div className={styles.unlock}>
            <Typography
              variant='heading-s'
              medium
              className={styles.subheading}
            >
              Upgrade and unlock:
            </Typography>
            <ul className={styles.benefitList}>
              {UPSELL_BENEFITS.map(benefit => (
                <li key={benefit}>
                  <Typography variant='body-s' color='grey'>
                    {benefit}
                  </Typography>
                </li>
              ))}
            </ul>
          </div>

          {/* Pinned to the modal's bottom; the benefit list scrolls and fades
              into the gradient above the CTA, hinting at more content below. */}
          <div className={styles.footer}>
            <LinkButton
              color='purple'
              size={ButtonSizes.M}
              width='full'
              href={upgradeLink}
              linkClassName={`button ${styles.ctaLink}`}
              onClick={handleUpgradeClick}
              external
            >
              Upgrade to Tiny+
            </LinkButton>

            <Button
              variant={ButtonVariants.TEXT}
              color={ButtonColors.PURPLE}
              size={ButtonSizes.M}
              label='Maybe later'
              onClick={handleDismiss}
              className={styles.maybeLater}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default UpgradeModal;
